import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';

import { Feature } from '../../_components/feature';
import { useConfig } from '../config';
import { useToastr } from '../toastr';

import styles from './styles.module.scss';

export interface DataLoaderButtonProps {
  actions: Record<string, any>;
}

export interface DataLoaderProps {
  mockData: Record<string, any>;
}
export const DataLoader = ({ mockData }: DataLoaderProps) => {
  const toastr = useToastr();
  const { api } = useConfig();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formData = useWatch();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);
  const [modalOpen, setModalOpen] = useState(false);
  const { setValue, trigger } = useFormContext();

  const setFormValues = useCallback(
    async (obj: object) => {
      const entries = Object.entries(obj);

      await Promise.all(
        entries.map(async ([name, value]) => {
          let newValue = value;
          if (typeof value === 'function') {
            newValue = await value(api);
          }
          setValue(name, newValue, { shouldDirty: true });
        })
      );
      trigger(Object.keys(obj));
    },
    [api, setValue, trigger]
  );

  const valueItems = useMemo(() => {
    return Object.entries(mockData).map(([name, value], index) => (
      <DropdownItem key={`value_${index}`} onClick={() => setFormValues(value)}>
        {name}
      </DropdownItem>
    ));
  }, [mockData, setFormValues]);

  const getCurrentText = () => {
    return (textareaRef.current as any)?.value;
  };
  const onCancel = () => {
    setModalOpen(false);
  };
  const onCopy = () => {
    navigator.clipboard.writeText(getCurrentText());
  };
  const onSave = () => {
    try {
      const newFormData = JSON.parse(getCurrentText());
      setFormValues(newFormData);
      setModalOpen(false);
    } catch (error: any) {
      toastr.error(<span>{error.message}</span>, {
        details: error,
      });
    }
  };

  return (
    <Feature id="demo">
      <div className="text-end">
        <Dropdown isOpen={dropdownOpen} toggle={toggle}>
          <DropdownToggle
            caret
            className={`align-self-start bg-warning p-2 ms-auto ${styles.demoButton} shadow`}
            aria-label="Mock data populator"
          >
            <FontAwesomeIcon size="lg" icon={faPencil} className="me-1" />
          </DropdownToggle>
          <DropdownMenu style={{ border: 'solid 1px rgba(0,0,0,0.3)' }}>
            <DropdownItem
              className="fw-bold"
              onClick={() => setModalOpen(true)}
            >
              COPY/PASTE DATA
            </DropdownItem>
            {valueItems}
          </DropdownMenu>
        </Dropdown>
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
          <ModalHeader>Copy/Paste Data</ModalHeader>
          <ModalBody>
            <textarea
              style={{ width: '100%', minHeight: '500px' }}
              ref={textareaRef}
              defaultValue={JSON.stringify(formData, null, 2)}
            />
            <Row className="d-flex justify-content-around">
              <Button onClick={onCancel}>Close</Button>
              <Button onClick={onCopy}>Copy Data</Button>
              <Button onClick={onSave}>Save</Button>
            </Row>
          </ModalBody>
        </Modal>
      </div>
    </Feature>
  );
};
